import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Sheet,
  Textarea,
  Typography,
} from "@mui/joy";
import {
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { CallTheme, interactorLabelColor } from "../entities/callDesign";
import { MissionMap, MissionMap_Entity } from "../entities/missionMap";
import { Inspector, Interactor } from "../entities/missionMap/missionMap";
import { inReplyOrder } from "../entities/replyOrder";
import { useContract } from "../hooks/nerdApp/nerdApp";
import { AskForRelease } from "../organisms/askForRelease";
import { Signature, Upload } from "../organisms/response";
import { SeasonCalendar } from "../organisms/seasonCalendar";
import { PartialAcceptWarning } from "../organisms/partialAcceptWarning";
import {
  detectPartialAcceptance,
  isAcceptInteractor,
  typedMessage,
} from "../partialAcceptance";
import {
  answersSelector,
  setAnswers,
  setSelectedBookletToken,
} from "../redux/rhapsodyReducer";
import {
  CallFooter,
  HAIRLINE,
  INK,
  ModernInviteContent,
  MUTED,
  QUILL_ALIGN_SX,
  SOFT,
  visibleCallSections,
} from "./modernInvite";

/**
 * The modern season invite (power-up `modernCalls`): one project at a time,
 * filling the screen, decided and left behind.
 *
 * The old page asked a musician to open each project in a side drawer, answer
 * it, close it and repeat — then refused to submit, naming whichever project
 * they had missed. Here the season is a DECK:
 *
 *   intro   whose season, how big, the greeting, the memo, the calendar
 *   deck    one project at a time with its WHOLE call on screen, answered by
 *           tapping a reply, swiping the card away, or an arrow key
 *   review  every answer in one list, changeable, then sent
 *
 * Nothing leaves the browser until Send, which is what makes a swipe safe for
 * a booking decision: any answer can be revisited from the review or by
 * walking back through the deck.
 */

/** How far a card must travel before release commits the answer. */
const SWIPE_COMMIT = 110;
/** How long the card takes to fly out before the next one arrives. */
const EXIT_MS = 240;

type Answers = {
  [token: string]: {
    inspectors?: { [i: number]: Inspector };
    selectedInteractor?: Interactor;
    selectedInteractorJobStageID?: number;
  };
};

/** Every project a musician still has to answer. Projects whose interactors
 * are gone were resolved upstream — listed on the review, never asked. */
function answerable(projects: MissionMap_Entity[]): MissionMap_Entity[] {
  return projects.filter((project) => project.Interactors?.length > 0);
}

/** Interactors in the order the ORGANIZATION configured them (Position), id
 * as a tiebreak. The mapping's own order is not stable — the same season
 * arrived Accept/Decline/Hold on one project and Hold/Accept/Decline on the
 * next — and rhapsody-service now sorts it, but a mapping stored before that
 * still needs putting straight here. */
function inConfiguredOrder(interactors: Interactor[]): Interactor[] {
  return [...interactors].sort(
    (a, b) => (a.Position ?? 0) - (b.Position ?? 0) || a.ID - b.ID,
  );
}

/** The reply buttons of a project, always decline → hold → accept. The
 * configured order still decides everything inReplyOrder treats as equal. */
function replyInteractors(project: MissionMap_Entity): Interactor[] {
  return inReplyOrder(
    inConfiguredOrder(
      (project.Interactors ?? []).filter(
        (interactor) =>
          interactor.Kind === "button" &&
          interactor.SegueID &&
          interactor.Description !== "contract",
      ),
    ),
  );
}

/** Everything else the org asks of a musician on this project — a message, an
 * upload, a signature, a download, a box to tick. Musicians must SEE these,
 * not discover them; they render above the replies in the org's order. */
function inputInteractors(project: MissionMap_Entity): Interactor[] {
  return inConfiguredOrder(
    (project.Interactors ?? []).filter(
      (interactor) =>
        !(
          interactor.Kind === "button" &&
          interactor.SegueID &&
          interactor.Description !== "contract"
        ),
    ),
  );
}

/** True when a gesture starts inside something a musician types in, draws on
 * or presses. Selecting the text of a message, or signing, must never throw
 * the card away. */
function fromControl(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;

  return Boolean(
    element?.closest?.(
      "input, textarea, select, canvas, button, a, [contenteditable='true'], [role='button']",
    ),
  );
}

/** The reply a hold means, when the org offers one — the third direction. */
function holdInteractorOf(project: MissionMap_Entity): Interactor | undefined {
  return replyInteractors(project).find(
    (interactor) =>
      !isAcceptInteractor(interactor) && /hold/i.test(interactor.Name ?? ""),
  );
}

/** The two replies a swipe can mean: right accepts, left is the plain "no".
 * A hold is never a swipe — it wants a message. */
function swipeReplies(project: MissionMap_Entity) {
  const interactors = replyInteractors(project);
  const accept = interactors.find((interactor) =>
    isAcceptInteractor(interactor),
  );
  const decline = interactors.find(
    (interactor) =>
      !isAcceptInteractor(interactor) &&
      !/hold/i.test(interactor.Name ?? "") &&
      interactor.ID !== accept?.ID,
  );

  return { accept, decline };
}

/** The interactor a typed message is filed under, when the org asks for one. */
function messageInteractor(project: MissionMap_Entity): Interactor | undefined {
  return (project.Interactors ?? []).find(
    (interactor) => interactor.Kind === "text",
  );
}

function sessionsOf(project: MissionMap_Entity) {
  return (project.Engagements ?? []).flatMap((group) => group.Content ?? []);
}

/** Sessions in order: sorted by the UTC instant (always comparable), never
 * labelled from it — the labels are the venue's own. */
function orderedSessions(projects: MissionMap_Entity[]) {
  return projects
    .flatMap((project) => sessionsOf(project))
    .filter((session) => session.Day && session.Month)
    .sort((a, b) => (a.DateFromUTC ?? "").localeCompare(b.DateFromUTC ?? ""));
}

/** A project's date range: the server's when it has one (identical to the
 * email's), else built from its sessions' labels. */
function dateRangeOf(project: MissionMap_Entity): string {
  if (project.DateRange) return project.DateRange;

  const sessions = orderedSessions([project]);
  if (!sessions.length) return "";

  const first = sessions[0];
  const last = sessions[sessions.length - 1];
  if (first === last) return `${first.Month} ${first.Day}`;
  if (first.Month === last.Month)
    return `${first.Month} ${first.Day} – ${last.Day}`;

  return `${first.Month} ${first.Day} – ${last.Month} ${last.Day}`;
}

function seasonRangeOf(mission: MissionMap_Entity): string {
  if (mission.SeasonDateRange) return mission.SeasonDateRange;

  const sessions = orderedSessions(mission.Projects ?? []);
  if (!sessions.length) return "";

  const first = sessions[0];
  const last = sessions[sessions.length - 1];
  if (first === last) return `${first.Month} ${first.Day}`;

  return `${first.Month} ${first.Day} – ${last.Month} ${last.Day}`;
}

/** The org's reply as a filled pill, for the review list. */
function AnswerChip({
  project,
  interactor,
}: {
  project: MissionMap_Entity;
  interactor: Interactor;
}) {
  return (
    <Typography
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: "4px",
        background: interactor.Color,
        color: interactorLabelColor(project, interactor),
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {interactor.Icon ? <i className={interactor.Icon} /> : null}
      {interactor.Name}
    </Typography>
  );
}

/**
 * The season before answering: artwork, shape, greeting, memo — the same
 * information design the email opens with — then what a musician may want
 * first: the calendar, their contract, onboarding, a release already asked.
 *
 * @param root0 props
 * @param root0.mission the season's public mapping
 * @param root0.theme resolved call theme
 * @param root0.projectCount how many projects are being asked
 * @param root0.shared replies every project offers, to answer in one go
 * @param root0.onStart opens the deck
 * @param root0.onAnswerAll answers every project with one reply
 * @returns {ReactElement} the intro screen
 */
/** Every project of the season, one row each: what it is, and where the
 * musician stands on it — their own answer while it is still theirs to
 * change, the organization's stage once the answer has been sent. Rows that
 * lead somewhere say so with a chevron; the rest are a statement, not a link.
 *
 * @param root0 props
 * @param root0.projects the season's projects, in the mapping's order
 * @param root0.answers what the musician has chosen but not yet sent
 * @param root0.canOpen whether a row leads anywhere
 * @param root0.onOpen follows the row
 * @returns {ReactElement} the list
 */
function SeasonProjectList({
  projects,
  answers,
  canOpen,
  onOpen,
}: {
  projects: MissionMap_Entity[];
  answers: Answers;
  canOpen: (project: MissionMap_Entity) => boolean;
  onOpen: (project: MissionMap_Entity) => void;
}) {
  return (
    <Box sx={{ mt: 2, borderTop: HAIRLINE }}>
      {projects.map((project) => {
        const chosen = answers[project.PublicToken]?.selectedInteractor;
        const asks = project.Interactors?.length > 0;
        const openable = canOpen(project);

        return (
          <Box
            key={project.PublicToken}
            onClick={() => {
              if (openable) onOpen(project);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: 1.25,
              borderBottom: "1px solid #f0f2f5",
              cursor: openable ? "pointer" : "default",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: INK }}>
                {project.ProjectName}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                {dateRangeOf(project)}
                {project.ScheduleDescription
                  ? ` · ${project.ScheduleDescription}`
                  : ""}
              </Typography>
            </Box>
            {chosen ? (
              <AnswerChip project={project} interactor={chosen} />
            ) : asks ? (
              <Typography sx={{ fontSize: 12.5, color: "#b4720c" }}>
                Not answered
              </Typography>
            ) : project.CurrentStage ? (
              <Typography
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: project.CurrentStage.Color,
                }}
              >
                {project.CurrentStage.Name}
              </Typography>
            ) : null}
            {openable ? (
              <Box
                component="i"
                className="fa-solid fa-chevron-right"
                sx={{ fontSize: 12, color: MUTED, flexShrink: 0 }}
              />
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

function SeasonIntro({
  mission,
  theme,
  projectCount,
  shared,
  onStart,
  onAnswerAll,
}: {
  mission: MissionMap_Entity;
  theme: CallTheme;
  projectCount: number;
  shared: Interactor[];
  onStart: () => void;
  onAnswerAll: (interactor: Interactor) => void;
}) {
  const dispatch = useDispatch();
  const { contract } = useContract();
  const seasonMission = useMemo(() => new MissionMap(mission), [mission]);

  const projects = mission.Projects ?? [];
  const serviceCount =
    mission.ServiceCount ??
    projects.reduce((total, project) => total + sessionsOf(project).length, 0);
  const range = seasonRangeOf(mission);

  const accentButton = {
    background: theme.accentText,
    fontWeight: 700,
    "&:hover": { background: theme.accentText },
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {mission.CoverURL ? (
          <Box
            component="img"
            src={mission.CoverURL}
            alt=""
            sx={{
              width: 108,
              height: "auto",
              alignSelf: "flex-start",
              borderRadius: "6px",
              display: "block",
            }}
          />
        ) : null}
        <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 26,
              lineHeight: 1.2,
              fontWeight: 700,
              letterSpacing: "-0.3px",
              color: INK,
            }}
          >
            {mission.SeasonName || mission.ProjectName}
          </Typography>
          <Typography sx={{ fontSize: 15, color: SOFT, mt: 0.5 }}>
            {projects.length} project{projects.length === 1 ? "" : "s"}
            {serviceCount
              ? ` · ${serviceCount} service${serviceCount === 1 ? "" : "s"}`
              : ""}
          </Typography>
          {range ? (
            <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>
              {range}
            </Typography>
          ) : null}
          {mission.MessengerName ? (
            <Typography sx={{ fontSize: 13.5, color: MUTED, mt: 1.25 }}>
              From {mission.MessengerName}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {mission.Greetings ? (
        <Box
          sx={{
            fontSize: 15,
            lineHeight: 1.55,
            color: INK,
            mt: 1.5,
            "& p": { margin: "0 0 8px 0" },
            "& p:last-child": { marginBottom: 0 },
            ...QUILL_ALIGN_SX,
          }}
          dangerouslySetInnerHTML={{ __html: mission.Greetings }}
        />
      ) : null}

      {mission.Memo ? (
        <Box
          sx={{
            width: "100%",
            background: "#fdf6ec",
            borderLeft: "3px solid #b4720c",
            px: 1.5,
            py: 1,
            mt: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#8a5709",
            }}
          >
            Memo
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#3d444d" }}>
            {mission.Memo}
          </Typography>
        </Box>
      ) : null}

      {projectCount ? (
        <Button
          fullWidth
          size="lg"
          onClick={onStart}
          endDecorator={<i className="fa-solid fa-arrow-right" />}
          sx={{ mt: 2.5, ...accentButton }}
        >
          Go through {projectCount} project{projectCount === 1 ? "" : "s"}
        </Button>
      ) : null}

      {/* for the musician whose answer is the same to all of it */}
      {shared.length && projectCount > 1 ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 12.5, color: MUTED, mb: 0.75 }}>
            Or answer everything at once:
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {shared.map((interactor) => (
              <Button
                key={interactor.ID}
                size="sm"
                variant="outlined"
                onClick={() => onAnswerAll(interactor)}
                startDecorator={
                  interactor.Icon ? (
                    <i
                      className={interactor.Icon}
                      style={{ color: interactor.Color }}
                    />
                  ) : undefined
                }
                sx={{
                  borderColor: "#dde2e8",
                  color: SOFT,
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: interactor.Color,
                    background: "#fbfcfd",
                  },
                }}
              >
                {interactor.Name} all
              </Button>
            ))}
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          mt: 2.5,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <SeasonCalendar />
        {contract?.signed() ? (
          <Button
            size="sm"
            variant="soft"
            color="neutral"
            startDecorator={<i className="fa-solid fa-file-contract" />}
            onClick={() =>
              dispatch(setSelectedBookletToken(mission.BookletToken))
            }
          >
            View my contract
          </Button>
        ) : null}
        {contract &&
        !contract.signed() &&
        !seasonMission.onboardingRequired() ? (
          <Button
            size="sm"
            startDecorator={<i className="fa-solid fa-file-contract" />}
            onClick={() =>
              dispatch(setSelectedBookletToken(mission.BookletToken))
            }
            sx={accentButton}
          >
            Sign my contract
          </Button>
        ) : null}
        {!contract?.signed() && seasonMission.onboardingRequired() ? (
          <Button
            size="sm"
            endDecorator={
              <i className="fa-solid fa-arrow-up-right-from-square" />
            }
            onClick={() => seasonMission.onboardNow()}
            sx={accentButton}
          >
            Onboard now
          </Button>
        ) : null}
      </Box>

      <Box sx={{ mt: 1 }}>
        <AskForRelease token={mission.PublicToken} season />
      </Box>
    </Box>
  );
}

/**
 * One thing the org asks of a musician besides the reply itself: a message, an
 * upload, a signature, a download, a box to tick. Same kinds the call page
 * renders, written straight into THIS project's inspectors — the deck owns the
 * token, so nothing depends on what is "selected" globally.
 *
 * @param root0 props
 * @param root0.interactor the interactor to render
 * @param root0.value what has been entered so far
 * @param root0.onChange records the entry
 * @returns {ReactElement} the input
 */
function InteractorInput({
  interactor,
  value,
  onChange,
}: {
  interactor: Interactor;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const label = (
    <Typography
      sx={{ fontSize: 12.5, fontWeight: 600, color: INK }}
      startDecorator={
        interactor.Icon ? <i className={interactor.Icon} /> : undefined
      }
    >
      {interactor.Name}
    </Typography>
  );

  switch (interactor.Kind) {
    case "text":
      return (
        <Box>
          {label}
          <Textarea
            minRows={2}
            placeholder={interactor.Description || "Leave a message"}
            value={typeof value === "string" ? value : ""}
            onChange={(event) => onChange(event.target.value)}
            sx={{ mt: 0.5 }}
          />
        </Box>
      );
    case "file":
      return (
        <Box>
          {label}
          <Upload
            interactor={interactor}
            files={Array.isArray(value) ? (value as File[]) : []}
            onAdd={(added) =>
              onChange([
                ...(Array.isArray(value) ? (value as File[]) : []),
                ...added,
              ])
            }
            onDelete={(removed) =>
              onChange(
                (Array.isArray(value) ? (value as File[]) : []).filter(
                  (file) => file.name !== removed.name,
                ),
              )
            }
          />
        </Box>
      );
    case "signature":
      return (
        <Box>
          {label}
          <Signature interactor={interactor} onChange={onChange} />
        </Box>
      );
    case "download":
      return (
        <Button
          variant="soft"
          color="neutral"
          startDecorator={
            interactor.Icon ? <i className={interactor.Icon} /> : undefined
          }
          onClick={() => {
            window.open(interactor.Description);
            onChange("Downloaded");
          }}
        >
          {interactor.Name}
        </Button>
      );
    case "checkbox":
      return (
        <Checkbox
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
          label={
            // A column, or Joy lays the caption inline with the label.
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {label}
              {interactor.Description ? (
                <Typography sx={{ fontSize: 12, color: MUTED }}>
                  {interactor.Description}
                </Typography>
              ) : null}
            </Box>
          }
        />
      );
    default:
      // Same words the call page uses for a kind it does not know — no new
      // vocabulary invented here.
      return (
        <Sheet variant="soft" color="neutral" sx={{ borderRadius: "8px", p: 1 }}>
          <Typography sx={{ fontSize: 12.5, color: SOFT }}>
            Interactor Not Supported
          </Typography>
        </Sheet>
      );
  }
}

/**
 * One project filling the screen: its whole call in a scrolling body, the
 * replies pinned beneath it, and the card itself draggable so a decision can
 * be thrown away sideways.
 *
 * @param root0 props
 * @param root0.project the project's own mapping
 * @param root0.theme resolved call theme
 * @param root0.answer the reply chosen so far, if any
 * @param root0.message the message typed so far, if any
 * @param root0.exiting the direction the card is flying out, if any
 * @param root0.onAnswer records a reply
 * @param root0.onMessage records a message for this project
 * @returns {ReactElement} the project card
 */
function ProjectStage({
  project,
  theme,
  answer,
  inspectors,
  exiting,
  onAnswer,
  onInspector,
}: {
  project: MissionMap_Entity;
  theme: CallTheme;
  answer?: Interactor;
  inspectors?: { [i: number]: Inspector };
  exiting?: "left" | "right" | "up" | null;
  onAnswer: (interactor: Interactor) => void;
  onInspector: (interactor: Interactor, value: unknown) => void;
}) {
  // Two axes: sideways answers yes/no, upward holds. Only the grip strip
  // (under the replies) reads vertical drags, because the card's body is a
  // scroller and a musician dragging there is reading, not answering.
  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const origin = useRef<{
    x: number;
    y: number;
    id: number;
    axis: "x" | "y";
  } | null>(null);

  const projectMission = useMemo(() => new MissionMap(project), [project]);
  const interactors = replyInteractors(project);
  const inputs = inputInteractors(project);
  const { accept, decline } = swipeReplies(project);
  const hold = holdInteractorOf(project);
  const swipeable = Boolean(accept || decline);

  const vertical = Math.abs(drag.y) > Math.abs(drag.x);
  const pending = vertical
    ? drag.y < 0
      ? hold
      : undefined
    : drag.x > 0
      ? accept
      : decline;
  const travelled = vertical ? Math.abs(drag.y) : Math.abs(drag.x);
  const reach = Math.min(travelled / SWIPE_COMMIT, 1);

  const grab =
    (axis: "x" | "y") => (event: ReactPointerEvent<HTMLDivElement>) => {
      if (exiting) return;
      if (axis === "x" && !swipeable) return;
      if (axis === "y" && !hold) return;
      if (fromControl(event.target)) return;
      origin.current = {
        x: event.clientX,
        y: event.clientY,
        id: event.pointerId,
        axis,
      };
    };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = origin.current;
    if (!start || start.id !== event.pointerId) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (start.axis === "x") {
      // Vertical intent inside the body is reading, not answering.
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 12) {
        origin.current = null;
        setDrag({ x: 0, y: 0 });
        return;
      }
      setDrag({ x: dx, y: 0 });
      return;
    }
    // From the grip: upward holds, and a sideways pull still answers.
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 12 && swipeable) {
      setDrag({ x: dx, y: 0 });
      return;
    }
    setDrag({ x: 0, y: Math.min(0, dy) });
  };

  const onPointerUp = () => {
    if (!origin.current) return;
    origin.current = null;
    const committed = travelled >= SWIPE_COMMIT ? pending : undefined;
    setDrag({ x: 0, y: 0 });
    if (committed) onAnswer(committed);
  };

  const flightX = exiting === "left" ? -560 : exiting === "right" ? 560 : 0;
  const flightY = exiting === "up" ? -680 : 0;
  const offsetX = exiting ? flightX : drag.x;
  const offsetY = exiting ? flightY : drag.y;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        border: HAIRLINE,
        borderRadius: "14px",
        background: "#ffffff",
        boxShadow: "0 10px 30px rgba(15, 18, 22, 0.07)",
        overflow: "hidden",
        transform: `translate(${offsetX}px, ${offsetY}px) rotate(${offsetX / 70}deg)`,
        opacity: exiting ? 0 : 1,
        transition:
          exiting || (drag.x === 0 && drag.y === 0)
            ? `transform ${EXIT_MS}ms ease-out, opacity ${EXIT_MS}ms ease-out`
            : "none",
        touchAction: "pan-y",
      }}
      onPointerDown={grab("x")}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      {/* what releasing right now would answer */}
      {travelled > 0 && pending ? (
        <>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: pending.Color,
              opacity: reach * 0.14,
              pointerEvents: "none",
              zIndex: 2,
            }}
          />
          <Typography
            sx={{
              position: "absolute",
              top: 16,
              ...(vertical
                ? { left: "50%", transform: "translateX(-50%)" }
                : { [drag.x > 0 ? "left" : "right"]: 18 }),
              zIndex: 3,
              px: 1.25,
              py: 0.5,
              borderRadius: "6px",
              background: pending.Color,
              color: interactorLabelColor(project, pending),
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              opacity: reach,
              pointerEvents: "none",
            }}
          >
            {pending.Name}
          </Typography>
        </>
      ) : null}

      {/* the project's whole call — the same content as its own call page */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 2,
          pt: 2,
          pb: 1,
          WebkitOverflowScrolling: "touch",
        }}
      >
        <ModernInviteContent
          mission={projectMission}
          theme={theme}
          token={project.PublicToken}
          hideResponse
          hideFooter
          hideGreeting
        />
      </Box>

      {/* the ask: everything the org requests, then Mercury's buttons in
          Mercury's colors and the org's own order */}
      <Box sx={{ borderTop: HAIRLINE, background: "#ffffff" }}>
        {inputs.length ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              px: 2,
              pt: 1.5,
            }}
          >
            {inputs.map((interactor) => (
              <InteractorInput
                key={interactor.ID}
                interactor={interactor}
                value={inspectors?.[interactor.ID]?.message}
                onChange={(value) => onInspector(interactor, value)}
              />
            ))}
          </Box>
        ) : null}
        <Box sx={{ display: "flex", gap: 1, px: 2, pt: 1.5 }}>
          {interactors.map((interactor) => {
            const chosen = answer?.ID === interactor.ID;
            return (
              <Button
                key={interactor.ID}
                size="lg"
                onClick={() => onAnswer(interactor)}
                startDecorator={
                  interactor.Icon ? <i className={interactor.Icon} /> : undefined
                }
                sx={{
                  flex: 1,
                  background: interactor.Color,
                  color: interactorLabelColor(project, interactor),
                  fontWeight: 700,
                  // A revisited project shows what was chosen, still changeable.
                  opacity: !answer || chosen ? 1 : 0.4,
                  "&:hover": { background: interactor.Color, opacity: 1 },
                }}
              >
                {interactor.Name}
              </Button>
            );
          })}
        </Box>
        {/* the grip: the one strip that reads a vertical drag, so an upward
            throw can mean hold without fighting the card's scroller */}
        <Box
          onPointerDown={(event) => {
            // The card's own handler would otherwise claim this gesture as
            // horizontal on the way up and cancel the hold.
            event.stopPropagation();
            grab("y")(event);
            // Capture the pointer, or the gesture is only ever as tall as this
            // strip. Without it the browser keeps delivering move/up to
            // whatever sits under the finger, and an upward throw leaves the
            // grip almost immediately — the card translates up with the drag,
            // so the strip slides out from under the pointer — after which no
            // pointerup ever reaches this handler and the hold never commits.
            // Every failed drag also left origin.current set, so the next
            // gesture started mid-flight.
            // Guarded: setPointerCapture throws NotFoundError when the pointer
            // id is no longer active, and a throw here would take the whole
            // gesture with it. Capture is an improvement, not a prerequisite —
            // without it the drag still works as far as the strip reaches.
            try {
              if (hold) event.currentTarget.setPointerCapture(event.pointerId);
            } catch {
              /* pointer already gone; the drag still reads without capture */
            }
          }}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          sx={{
            px: 2,
            pt: 1,
            pb: 1.5,
            cursor: hold ? "grab" : "default",
            // "none", not "pan-y": pan-y hands vertical gestures to the browser
            // as a scroll, so on a touch device the drag this strip exists to
            // read was cancelled before it travelled far enough to mean hold.
            touchAction: hold ? "none" : "pan-y",
            // A drag has to start on something unselectable, or the press
            // anchors a native text selection instead of a gesture: the drag
            // paints its way up through the schedule, and the browser fires
            // pointercancel the moment it takes the gesture over — which lands
            // on onPointerUp and cancels the hold before it can commit.
            //
            // Only this strip, deliberately. A press here can mean nothing but
            // a drag, whereas on the card body it may well be someone selecting
            // the venue address to copy it, so the body stays selectable.
            //
            // Prefixed for Safari and iOS, which is where most of these calls
            // are answered and where user-select is still unprefixed-last.
            userSelect: "none",
            WebkitUserSelect: "none",
            // And no long-press callout on iOS, the other way a press on this
            // strip turns into a text interaction rather than a drag.
            WebkitTouchCallout: "none",
          }}
        >
          <Typography sx={{ fontSize: 12, color: MUTED, textAlign: "center" }}>
            {accept ? `Swipe right to ${accept.Name.toLowerCase()}` : ""}
            {decline ? `, left to ${decline.Name.toLowerCase()}` : ""}
            {hold ? `, or drag up from here to ${hold.Name.toLowerCase()}` : ""}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * @param root0 props
 * @param root0.mission the season's public mapping
 * @param root0.theme resolved call theme (modern only — the caller gates)
 * @param root0.submitting whether a submission is in flight
 * @param root0.onSubmit sends every answer
 * @returns {ReactElement} the season invite
 */
export function ModernSeasonInvite({
  mission,
  theme,
  submitting,
  onSubmit,
}: {
  mission: MissionMap_Entity;
  theme: CallTheme;
  submitting?: boolean;
  onSubmit: () => void;
}) {
  const dispatch = useDispatch();
  const answers = useSelector(answersSelector) as Answers;
  const projects = mission.Projects ?? [];
  const asked = useMemo(() => answerable(projects), [projects]);
  // sameInteractors() returns them in the mapping's order, which is the order
  // nobody can rely on — the "answer all" chips get the same treatment.
  // Same order as the cards below: "Decline all" sits where "Decline" does, so
  // the bulk row is not a differently-arranged copy of the buttons the musician
  // is about to see three of.
  const shared = useMemo(
    () => inReplyOrder(inConfiguredOrder(new MissionMap(mission).sameInteractors())),
    [mission],
  );

  // Where the musician is: the intro, a project of the deck, the review, or —
  // once there is nothing left to answer — the record of what they sent.
  const [stage, setStage] = useState<"intro" | "deck" | "review" | "summary">(
    () => (projects.length && !answerable(projects).length ? "summary" : "intro"),
  );
  // The project being read from the summary, full screen like the deck's.
  // Held by token rather than by the project itself: the mapping refetches on
  // its own (an answer elsewhere, a poll), and a captured copy would go on
  // showing what the season no longer says.
  const [opened, setOpened] = useState<string>();
  const [index, setIndex] = useState(0);
  const [exiting, setExiting] = useState<"left" | "right" | "up" | null>(null);
  // Set when an acceptance is about to go out on a project whose message reads
  // like the musician can only play part of it.
  const [partialAccept, setPartialAccept] = useState<{
    project: MissionMap_Entity;
    accept: Interactor;
  }>();

  // Nothing left to ask means the season is answered, and the flow for
  // answering it has to give way to the record of it. This is also the
  // post-submit transition: sending invalidates the mapping, the refetch comes
  // back with the interactors gone, and without this the review the musician
  // sent from stayed on screen — live Send button and all — over answers the
  // server already had.
  useEffect(() => {
    if (!asked.length && projects.length) setStage("summary");
  }, [asked.length, projects.length]);

  // Sent answers belong to the mapping now. The browser's copy was only ever
  // the draft, and left in place it paints the musician's own chip over the
  // organization's status — the one thing the summary exists to show.
  useEffect(() => {
    if (stage !== "summary") return;
    const stale = projects.filter(
      (project) => answers[project.PublicToken],
    );
    if (!stale.length) return;
    const next = { ...answers };
    stale.forEach((project) => delete next[project.PublicToken]);
    dispatch(setAnswers(next));
  }, [stage, projects, answers, dispatch]);

  // Which projects still have a page worth opening. A musician who declined
  // keeps the limited view they have always had — the schedule leaves with the
  // seat (see showsSchedule) — so a declined project with nothing else left is
  // a status, not a link.
  const openable = useMemo(
    () =>
      projects
        .filter(
          (project) => visibleCallSections(new MissionMap(project)).length > 0,
        )
        .map((project) => project.PublicToken),
    [projects],
  );

  const openedProject = useMemo(
    () => projects.find((project) => project.PublicToken === opened),
    [projects, opened],
  );
  const openedMission = useMemo(
    () => (openedProject ? new MissionMap(openedProject) : undefined),
    [openedProject],
  );

  const current = asked[index];
  const answeredCount = asked.filter(
    (project) => answers[project.PublicToken]?.selectedInteractor,
  ).length;
  const remaining = asked.length - answeredCount;

  const firstUnanswered = () => {
    const at = asked.findIndex(
      (project) => !answers[project.PublicToken]?.selectedInteractor,
    );

    return at < 0 ? 0 : at;
  };

  /** The inspector set an answer carries, so a reply chosen in the deck
   * submits exactly like one chosen inside a single project's own call. */
  const inspectorsFor = (project: MissionMap_Entity) => {
    const existing = answers[project.PublicToken]?.inspectors ?? {};

    return (project.Interactors ?? []).reduce<{ [i: number]: Inspector }>(
      (all, interactor) => {
        all[interactor.ID] = {
          ...(existing[interactor.ID] ?? {}),
          id: interactor.ID,
          title: interactor.Name,
          icon: interactor.Icon,
          kind: interactor.Kind,
        };
        return all;
      },
      {},
    );
  };

  const write = (
    project: MissionMap_Entity,
    patch: Partial<Answers[string]>,
  ) => {
    dispatch(
      setAnswers({
        ...answers,
        [project.PublicToken]: {
          ...(answers[project.PublicToken] ?? {}),
          inspectors: inspectorsFor(project),
          ...patch,
        },
      }),
    );
  };

  /** Records whatever a musician entered for one interactor of one project —
   * a message, a file list, a signature, a tick — under that project's own
   * token, which is what the submit then uploads and sends. */
  const recordInspector = (
    project: MissionMap_Entity,
    interactor: Interactor,
    value: unknown,
  ) => {
    const inspectors = inspectorsFor(project);
    inspectors[interactor.ID] = {
      ...inspectors[interactor.ID],
      message: value,
    };
    write(project, { inspectors });
  };

  /** Answer the project on screen, fly it away, bring on the next one. */
  const commit = (project: MissionMap_Entity, interactor: Interactor) => {
    write(project, { selectedInteractor: interactor });
    // The card leaves the way the answer was given: yes right, hold up, no left.
    const hold = holdInteractorOf(project);
    setExiting(
      isAcceptInteractor(interactor)
        ? "right"
        : interactor.ID === hold?.ID
          ? "up"
          : "left",
    );
    // A timer, not transitionend: the flow must advance even where the
    // animation never runs (reduced motion, a backgrounded tab).
    window.setTimeout(() => {
      setExiting(null);
      setIndex((at) => {
        if (at + 1 >= asked.length) {
          setStage("review");
          return at;
        }
        return at + 1;
      });
    }, EXIT_MS);
  };

  /**
   * Accepting a project while the message explains you can only play part of
   * it tells the contractor two opposite things. Offer Hold first — the same
   * guard the single call has, which the deck bypassed by answering directly.
   *
   * @param {MissionMap_Entity} project the project being answered
   * @param {Interactor} interactor the reply the musician chose
   */
  const answer = (project: MissionMap_Entity, interactor: Interactor) => {
    const hold = holdInteractorOf(project);
    if (hold && isAcceptInteractor(interactor)) {
      const message = typedMessage(answers[project.PublicToken]?.inspectors);
      if (detectPartialAcceptance(message).partial) {
        setPartialAccept({ project, accept: interactor });
        return;
      }
    }
    commit(project, interactor);
  };

  const answerAll = (interactor: Interactor) => {
    const next: Answers = { ...answers };
    asked.forEach((project) => {
      const match = (project.Interactors ?? []).find(
        (candidate) => candidate.ID === interactor.ID,
      );
      if (!match) return;
      next[project.PublicToken] = {
        ...(next[project.PublicToken] ?? {}),
        inspectors: inspectorsFor(project),
        selectedInteractor: match,
      };
    });
    dispatch(setAnswers(next));
    setStage("review");
  };

  /** Forget every answer for THIS season, leaving any other token in the
   * store alone, and start again from the intro. */
  const clearAnswers = () => {
    const next: Answers = { ...answers };
    asked.forEach((project) => delete next[project.PublicToken]);
    dispatch(setAnswers(next));
    setIndex(0);
    setStage("intro");
  };

  const back = () => {
    if (index === 0) {
      setStage("intro");
      return;
    }
    setIndex((at) => Math.max(0, at - 1));
  };

  // Arrow keys answer on a keyboard — unless something is being typed into.
  useEffect(() => {
    if (stage !== "deck" || !current || exiting) return undefined;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      )
        return;
      const { accept, decline } = swipeReplies(current);
      const hold = holdInteractorOf(current);
      if (event.key === "ArrowRight" && accept) {
        event.preventDefault();
        answer(current, accept);
      }
      if (event.key === "ArrowLeft" && decline) {
        event.preventDefault();
        answer(current, decline);
      }
      if (event.key === "ArrowUp" && hold) {
        event.preventDefault();
        answer(current, hold);
      }
    };
    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [stage, current, exiting, answers]);

  const column = {
    width: "100%",
    fontFamily: theme.fontStack,
    "& .MuiTypography-root, & .MuiButton-root, & .MuiTextarea-root": {
      fontFamily: "inherit",
    },
  };

  if (stage === "intro")
    return (
      <Box sx={column}>
        <SeasonIntro
          mission={mission}
          theme={theme}
          projectCount={asked.length}
          shared={shared}
          onStart={() => {
            setIndex(firstUnanswered());
            setStage(asked.length ? "deck" : "review");
          }}
          onAnswerAll={answerAll}
        />
        <CallFooter companyName={mission.CompanyName} />
      </Box>
    );

  if (stage === "deck" && current)
    return (
      <Box
        sx={{
          ...column,
          // The deck owns the screen: the card takes what is left under the
          // masthead, and only the card's own body scrolls.
          height: "calc(100vh - 140px)",
          minHeight: 440,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
        }}
      >
        {/* where you are in the season */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="sm"
            variant="plain"
            aria-label="Previous project"
            onClick={back}
            sx={{ color: MUTED }}
          >
            <i className="fa-solid fa-chevron-left" />
          </IconButton>
          <Box sx={{ flex: 1, display: "flex", gap: 0.5 }}>
            {asked.map((project, at) => {
              const done = Boolean(
                answers[project.PublicToken]?.selectedInteractor,
              );
              return (
                <Box
                  key={project.PublicToken}
                  onClick={() => setIndex(at)}
                  aria-label={project.ProjectName}
                  sx={{
                    flex: 1,
                    height: 3,
                    borderRadius: 2,
                    cursor: "pointer",
                    background:
                      at === index
                        ? theme.accentText
                        : done
                          ? "#c7d2de"
                          : "#eef1f4",
                  }}
                />
              );
            })}
          </Box>
          <Typography
            sx={{ fontSize: 12.5, color: MUTED, whiteSpace: "nowrap" }}
          >
            {index + 1} of {asked.length}
          </Typography>
          <Typography
            onClick={() => setStage("review")}
            sx={{
              fontSize: 12.5,
              color: theme.accentText,
              textDecoration: "underline",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Review
          </Typography>
        </Box>

        <PartialAcceptWarning
          accept={partialAccept?.accept}
          hold={
            partialAccept ? holdInteractorOf(partialAccept.project) : undefined
          }
          companyName={mission.CompanyName}
          onHold={() => {
            const pending = partialAccept;
            setPartialAccept(undefined);
            const hold = pending && holdInteractorOf(pending.project);
            if (pending && hold) commit(pending.project, hold);
          }}
          onAccept={() => {
            const pending = partialAccept;
            setPartialAccept(undefined);
            if (pending) commit(pending.project, pending.accept);
          }}
          onEdit={() => setPartialAccept(undefined)}
        />
        <ProjectStage
          key={current.PublicToken}
          project={current}
          theme={theme}
          answer={answers[current.PublicToken]?.selectedInteractor}
          inspectors={answers[current.PublicToken]?.inspectors}
          exiting={exiting}
          onAnswer={(interactor) => answer(current, interactor)}
          onInspector={(interactor, value) =>
            recordInspector(current, interactor, value)
          }
        />
      </Box>
    );

  if (stage === "summary" && openedMission && openedProject)
    return (
      <Box sx={column}>
        <Typography
          onClick={() => setOpened(undefined)}
          startDecorator={<i className="fa-solid fa-chevron-left" />}
          sx={{
            fontSize: 13,
            color: theme.accentText,
            cursor: "pointer",
            mb: 1,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {mission.SeasonName || mission.ProjectName || "All projects"}
        </Typography>
        <ModernInviteContent
          mission={openedMission}
          theme={theme}
          token={openedProject.PublicToken}
        />
      </Box>
    );

  // summary: the season after answering — what was decided, project by
  // project, and a way back into each one that still has anything to say.
  if (stage === "summary")
    return (
      <Box sx={column}>
        <SeasonIntro
          mission={mission}
          theme={theme}
          projectCount={0}
          shared={shared}
          onStart={() => setStage("deck")}
          onAnswerAll={answerAll}
        />
        <Typography sx={{ fontSize: 13.5, color: MUTED, mt: 2.5 }}>
          Your answers are in — here is where each project stands.
        </Typography>
        <SeasonProjectList
          projects={projects}
          answers={answers}
          // A project opens when it still has something to show. A musician
          // who declined keeps the limited view they have always had: the
          // schedule leaves with the seat (see showsSchedule), so a declined
          // project with nothing else left is a status, not a page.
          canOpen={(project) => openable.includes(project.PublicToken)}
          onOpen={(project) => {
            setOpened(project.PublicToken);
            // The list may be scrolled well past the top; the project opens at
            // its own beginning, the way the deck's cards do.
            window.scrollTo({ top: 0 });
          }}
        />
        <CallFooter companyName={mission.CompanyName} />
      </Box>
    );

  // review: every answer in one place, then send
  return (
    <Box sx={column}>
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 700,
          color: INK,
          letterSpacing: "-0.2px",
        }}
      >
        {remaining > 0 ? `${remaining} left to answer` : "Ready to send"}
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: MUTED, mt: 0.5 }}>
        {mission.SeasonName || mission.ProjectName} — tap a project to change
        your answer.
      </Typography>
      {answeredCount ? (
        <Typography
          onClick={clearAnswers}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            mt: 1,
            fontSize: 12.5,
            color: SOFT,
            textDecoration: "underline",
            cursor: "pointer",
            "&:hover": { color: INK },
          }}
        >
          <i className="fa-regular fa-delete-left" style={{ fontSize: 12 }} />
          Clear my {answeredCount === 1 ? "answer" : "answers"} and start over
        </Typography>
      ) : null}

      <SeasonProjectList
        projects={projects}
        answers={answers}
        canOpen={(project) =>
          asked.some(
            (candidate) => candidate.PublicToken === project.PublicToken,
          )
        }
        onOpen={(project) => {
          setIndex(
            asked.findIndex(
              (candidate) => candidate.PublicToken === project.PublicToken,
            ),
          );
          setStage("deck");
        }}
      />

      <Button
        fullWidth
        size="lg"
        disabled={submitting}
        startDecorator={submitting ? <CircularProgress size="sm" /> : undefined}
        onClick={() => {
          if (remaining > 0) {
            setIndex(firstUnanswered());
            setStage("deck");
            return;
          }
          onSubmit();
        }}
        sx={{
          mt: 2.5,
          background: remaining > 0 ? "#eef1f4" : theme.accentText,
          color: remaining > 0 ? SOFT : "#ffffff",
          fontWeight: 700,
          "&:hover": { background: remaining > 0 ? "#e6eaef" : theme.accentText },
        }}
      >
        {remaining > 0
          ? `Answer the last ${remaining}`
          : `Send my answer${asked.length > 1 ? "s" : ""}`}
      </Button>

      <CallFooter companyName={mission.CompanyName} />
    </Box>
  );
}
